import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, BadgeCheck, ChevronDown, HelpCircle, Pencil, Plus, Square, Trash2, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useHasPermission } from '@/hooks/useSession';
import { formatCurrency, formatDate } from '@/lib/utils';
import {
  useAddSubscriptionLine,
  useEndSubscriptionLine,
  useRemoveCoveredAccount,
  useUpdateSubscriptionLine,
  useVerifySubscriptionLine,
} from '../../hooks/useSubscriptionLines';
import { hasUnverifiedActiveLines, isLineActive, montrealToday } from '../../lineTypes';
import type { SubscriptionCeiling, SubscriptionCoveredAccount, SubscriptionLine, SubscriptionLineInput, SubscriptionLineUpdate } from '../../lineTypes';
import { LineFormDialog } from './LineFormDialog';
import { CoveredAccountDialog } from './CoveredAccountDialog';

interface SubscriptionLinesTabProps {
  subscriptionId: string;
  currency: string;
  /** Absent when the server predates the feature: "not deployed", never "none". */
  lines?: SubscriptionLine[];
  coveredAccounts?: SubscriptionCoveredAccount[];
  ceiling?: SubscriptionCeiling | null;
}

type LineDialogState = { mode: 'add' } | { mode: 'edit'; line: SubscriptionLine } | null;

const HEAD = 'px-2 py-2 font-medium text-xs';
const CELL = 'px-2 py-1.5 text-xs';

// #1407 — "shown expanded until the user closes it": the × writes the
// preference, the chevron only collapses for the session, "Help" clears it.
const HELP_DISMISSED_KEY = 'atek-subscriptions-lines-help-dismissed';

function isHelpDismissed(): boolean {
  try {
    return localStorage.getItem(HELP_DISMISSED_KEY) === '1';
  } catch { /* private mode etc. — banner just falls back to always-expanded */ }
  return false;
}

function setHelpDismissed(dismissed: boolean): void {
  try {
    if (dismissed) localStorage.setItem(HELP_DISMISSED_KEY, '1');
    else localStorage.removeItem(HELP_DISMISSED_KEY);
  } catch { /* ignore */ }
}

export function SubscriptionLinesTab({ subscriptionId, currency, lines, coveredAccounts, ceiling }: SubscriptionLinesTabProps) {
  const { t } = useTranslation('subscriptions');
  if (!lines) {
    return <p data-testid="lines-not-deployed" className="text-sm text-muted-foreground py-6 text-center">{t('lines.notDeployed')}</p>;
  }
  return <LinesTabBody subscriptionId={subscriptionId} currency={currency} lines={lines} coveredAccounts={coveredAccounts} ceiling={ceiling} />;
}

function LinesTabBody({ subscriptionId, currency, lines, coveredAccounts, ceiling }: SubscriptionLinesTabProps & { lines: SubscriptionLine[] }) {
  const { t } = useTranslation('subscriptions');
  // Same permission PUT /v4/subscriptions/:id enforces (D11).
  const canModify = useHasPermission('subscriptions.modify');

  const addLine = useAddSubscriptionLine(subscriptionId);
  const updateLine = useUpdateSubscriptionLine(subscriptionId);
  const endLine = useEndSubscriptionLine(subscriptionId);
  const verifyLine = useVerifySubscriptionLine(subscriptionId);
  const removeCovered = useRemoveCoveredAccount(subscriptionId);

  const [lineDialog, setLineDialog] = useState<LineDialogState>(null);
  const [endingLine, setEndingLine] = useState<SubscriptionLine | null>(null);
  const [removingCovered, setRemovingCovered] = useState<SubscriptionCoveredAccount | null>(null);
  const [coverOpen, setCoverOpen] = useState(false);

  const sorted = [...lines].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const handleSaveLine = async (data: SubscriptionLineInput | SubscriptionLineUpdate) => {
    if (lineDialog?.mode === 'edit') {
      await updateLine.mutateAsync({ lineId: lineDialog.line.id, data });
    } else {
      await addLine.mutateAsync(data as SubscriptionLineInput);
    }
    setLineDialog(null);
  };

  return (
    <div className="space-y-6 mt-4">
      <LinesHelpBanner />
      {ceiling?.pct != null && ceiling.pct >= 80 && (
        <Alert data-testid="lines-ceiling-banner" className="border-warning/50">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{t('lines.ceilingWarning', { pct: Math.floor(ceiling.pct) })}</AlertDescription>
        </Alert>
      )}
      {hasUnverifiedActiveLines(lines) && (
        <Alert data-testid="lines-unverified-banner" className="border-warning/50">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{t('lines.unverifiedBanner')}</AlertDescription>
        </Alert>
      )}

      <section className="space-y-3">
        <SectionHeader title={t('lines.title', { count: lines.length })} canModify={canModify} addLabel={t('lines.add')} onAdd={() => setLineDialog({ mode: 'add' })} />
        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">{t('lines.empty')}</p>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className={`${HEAD} text-left`}>{t('lines.sku')}</th>
                  <th className={`${HEAD} text-left`}>{t('lines.description')}</th>
                  <th className={`${HEAD} text-right`}>{t('lines.quantity')}</th>
                  <th className={`${HEAD} text-right`}>{t('lines.unitPrice')}</th>
                  <th className={`${HEAD} text-right`}>{t('lines.discount')}</th>
                  <th className={`${HEAD} text-left`}>{t('lines.basis')}</th>
                  <th className={`${HEAD} text-left`}>{t('lines.verified')}</th>
                  <th className={`${HEAD} text-left`}>{t('lines.ended')}</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((line) => (
                  <LineRow
                    key={line.id}
                    line={line}
                    currency={currency}
                    canModify={canModify}
                    verifying={verifyLine.isPending}
                    onVerify={() => verifyLine.mutate(line.id)}
                    onEdit={() => setLineDialog({ mode: 'edit', line })}
                    onEnd={() => setEndingLine(line)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {coveredAccounts && <section className="space-y-3" data-testid="covered-section">
        <SectionHeader
          title={t('lines.covered.title', { count: coveredAccounts.length })}
          canModify={canModify}
          addLabel={t('lines.covered.add')}
          onAdd={() => setCoverOpen(true)}
        />
        {coveredAccounts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t('lines.covered.empty')}</p>
        ) : (
          <ul className="border rounded-lg divide-y">
            {coveredAccounts.map((c) => (
              <CoveredRow key={c.accountId} covered={c} canModify={canModify} onRemove={() => setRemovingCovered(c)} />
            ))}
          </ul>
        )}
      </section>}

      <LineFormDialog
        open={!!lineDialog}
        line={lineDialog?.mode === 'edit' ? lineDialog.line : null}
        isSubmitting={addLine.isPending || updateLine.isPending}
        onOpenChange={(open) => { if (!open) setLineDialog(null); }}
        onSubmit={handleSaveLine}
      />

      <CoveredAccountDialog subscriptionId={subscriptionId} open={coverOpen} onOpenChange={setCoverOpen} />

      <ConfirmDialog
        open={!!endingLine}
        title={t('lines.endConfirm.title')}
        description={t('lines.endConfirm.description', { sku: endingLine?.sku ?? '' })}
        confirmLabel={t('lines.end')}
        onCancel={() => setEndingLine(null)}
        onConfirm={() => {
          if (endingLine) endLine.mutate({ lineId: endingLine.id, endedAt: montrealToday() });
          setEndingLine(null);
        }}
      />

      <ConfirmDialog
        open={!!removingCovered}
        title={t('lines.covered.removeConfirm.title')}
        description={t('lines.covered.removeConfirm.description', {
          account: removingCovered?.accountName ?? removingCovered?.accountNumber ?? '',
        })}
        confirmLabel={t('lines.covered.remove')}
        onCancel={() => setRemovingCovered(null)}
        onConfirm={() => {
          if (removingCovered) removeCovered.mutate(removingCovered.accountId);
          setRemovingCovered(null);
        }}
      />
    </div>
  );
}

/**
 * In-tab help (#1407): what the Lines tab bills and how to verify a line — the
 * guide account managers had nowhere in the UI. Collapsible via the chevron;
 * the × dismisses it for good (localStorage), "Help" brings it back for good.
 */
function LinesHelpBanner() {
  const { t } = useTranslation('subscriptions');
  const [dismissed, setDismissed] = useState(isHelpDismissed);
  const [open, setOpen] = useState(true);

  if (dismissed) {
    return (
      <Button
        type="button" variant="ghost" size="sm"
        className="h-7 px-2 text-xs text-muted-foreground"
        data-testid="lines-help-reopen"
        onClick={() => { setHelpDismissed(false); setDismissed(false); setOpen(true); }}
      >
        <HelpCircle className="h-3.5 w-3.5 mr-1" />
        {t('lines.help.reopen')}
      </Button>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg border bg-muted/30" data-testid="lines-help-banner">
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <CollapsibleTrigger asChild>
            <Button
              type="button" variant="ghost" size="sm"
              className="h-7 gap-1.5 px-2 text-sm font-medium"
              data-testid="lines-help-toggle" aria-expanded={open}
            >
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
              {t('lines.help.title')}
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? '' : 'rotate-180'}`} />
            </Button>
          </CollapsibleTrigger>
          <Button
            type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0"
            data-testid="lines-help-close" title={t('lines.help.close')} aria-label={t('lines.help.close')}
            onClick={() => { setHelpDismissed(true); setDismissed(true); }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <CollapsibleContent>
          <div data-testid="lines-help-body" className="space-y-2 border-t px-4 py-3 text-sm text-muted-foreground">
            <p>{t('lines.help.intro')}</p>
            <div>
              <p className="font-medium text-foreground">{t('lines.help.howTo')}</p>
              <ol className="mt-1 list-decimal space-y-1 pl-5">
                <li>{t('lines.help.step1')}</li>
                <li>
                  {t('lines.help.step2')}
                  <ul className="mt-1 list-disc space-y-0.5 pl-5">
                    <li>{t('lines.help.basisPerSensor')}</li>
                    <li>{t('lines.help.basisPerCalibration')}</li>
                    <li>{t('lines.help.basisPerSim')}</li>
                    <li>{t('lines.help.basisFixed')}</li>
                  </ul>
                </li>
                <li>{t('lines.help.step3')}</li>
                <li>{t('lines.help.step4')}</li>
              </ol>
            </div>
            <p className="italic">{t('lines.help.note')}</p>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function SectionHeader({ title, canModify, addLabel, onAdd }: { title: string; canModify: boolean; addLabel: string; onAdd: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-medium">{title}</h3>
      {canModify && (
        <Button type="button" variant="outline" size="sm" onClick={onAdd}>
        <Plus className="h-4 w-4 mr-1" />
        {addLabel}
      </Button>
      )}
    </div>
  );
}

interface LineRowProps {
  line: SubscriptionLine;
  currency: string;
  canModify: boolean;
  verifying: boolean;
  onVerify: () => void;
  onEdit: () => void;
  onEnd: () => void;
}

function LineRow({ line, currency, canModify, verifying, onVerify, onEdit, onEnd }: LineRowProps) {
  const { t } = useTranslation('subscriptions');
  const active = isLineActive(line);

  return (
    <tr data-testid={`line-row-${line.id}`} className={active ? 'border-t' : 'border-t text-muted-foreground'}>
      <td className={`${CELL} font-mono`}>{line.sku}</td>
      <td className={`${CELL} max-w-56 truncate`}>{line.description || '—'}</td>
      <td className={`${CELL} text-right tabular-nums`}>{Number(line.quantity)}</td>
      <td className={`${CELL} text-right tabular-nums`}>{formatCurrency(Number(line.unitPrice), currency)}</td>
      <td className={`${CELL} text-right tabular-nums`}>{Number(line.discount) > 0 ? `${Number(line.discount)}%` : '—'}</td>
      <td className={CELL} data-testid="line-basis">
        <Badge variant="outline" className="text-[10px]">{t(`lines.bases.${line.basis}`, line.basis)}</Badge>
      </td>
      <td className={CELL} data-testid="line-verified">
        <VerifiedCell line={line} />
      </td>
      <td className={CELL} data-testid="line-ended">{line.endedAt ? formatDate(line.endedAt) : '—'}</td>
      <td className="px-2 py-1.5">
        {active && canModify && (
          <div className="flex items-center justify-end gap-1">
            {!line.verified && (
              <Button
                type="button" variant="outline" size="sm" className="h-6 text-xs"
                data-testid={`line-verify-${line.id}`} disabled={verifying} onClick={onVerify}
              >
                {t('lines.verify')}
              </Button>
            )}
            <Button
              type="button" variant="ghost" size="icon" className="h-6 w-6"
              data-testid={`line-edit-${line.id}`} title={t('lines.edit')} onClick={onEdit}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button" variant="ghost" size="icon" className="h-6 w-6 hover:text-destructive"
              data-testid={`line-end-${line.id}`} title={t('lines.end')} onClick={onEnd}
            >
              <Square className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}

function VerifiedCell({ line }: { line: SubscriptionLine }) {
  const { t } = useTranslation('subscriptions');
  if (!line.verified) {
    return <Badge variant="warning" className="text-[10px]">{t('lines.unverified')}</Badge>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      <BadgeCheck className="h-3.5 w-3.5 text-success" />
      {line.verifiedByName || line.verifiedBy || ''}
      {line.verifiedAt ? ` · ${formatDate(line.verifiedAt)}` : ''}
    </span>
  );
}

function CoveredRow({ covered, canModify, onRemove }: { covered: SubscriptionCoveredAccount; canModify: boolean; onRemove: () => void }) {
  const { t } = useTranslation('subscriptions');
  return (
    <li data-testid={`covered-row-${covered.accountId}`} className="flex items-start justify-between gap-3 px-3 py-2 text-xs">
      <div className="space-y-0.5">
        <div className="font-medium">
          <span className="font-mono">{covered.accountNumber || '—'}</span> {covered.accountName || ''}
        </div>
        <div className="text-muted-foreground">{covered.reason}</div>
        <div className="text-muted-foreground">
          {t('lines.covered.addedBy', { who: covered.addedByName || covered.addedBy || '—', when: formatDate(covered.addedAt) })}
        </div>
      </div>
      {canModify && (
        <Button
        type="button" variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive"
        data-testid={`covered-remove-${covered.accountId}`} title={t('lines.covered.remove')} onClick={onRemove}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
      )}
    </li>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmDialog({ open, title, description, confirmLabel, onCancel, onConfirm }: ConfirmDialogProps) {
  const { t } = useTranslation('subscriptions');
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common:cancel', 'Cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
